import { TableProps, TableVirtuoso } from 'react-virtuoso';
import {
  DataTableSortableProps,
  DataTableRow,
  DataTableSortStateProps,
  DataTableVariant,
} from '../TableTypes';
import { TableLoader } from '../TableLoader';
import { StyledTableBody, StyledTableHead } from '../StyledTableComponents';
import { Table, TableRowProps, TableRow as MuiTableRow, Stack } from '@mui/material';
import { forwardRef, RefObject, useCallback, useMemo, useRef, useState } from 'react';
import TableRow from '../TableRow';
import { SortOrder } from '../../types';
import { ReachedLastRow } from '../ReachedLastRow';
import { colors } from '@sweep-io/sweep-design';
import { DraggableTableHead } from '../DraggableTableHead';
import { useCustomColumnsTableContext } from './CustomColumnsTableContext';
import { reorderItemInArray } from '../../../../lib/reorderItemInArray';
import { useManipulateColumns } from './useManipulateColumns';
import { DataTableId } from '../utils';

/**
 * Supports out of the box support of virtual scrolling for smooth big amounts of data rendering
 */
export function VirtualScrollCustomColumnsTable<TRow extends DataTableRow>({
  rows,
  onRowClick,
  containerRef,
  onSortChange,
  defaultSortState,
  tableEmptyStateJsx,
  sxRowFunction,
  isLoading,
  variant = DataTableVariant.medium,
  reachedLastRow,
  hideChevronButton,
  noTableBorder,
  tableId,
}: Pick<
  DataTableSortableProps<TRow>,
  | 'onRowClick'
  | 'rows'
  | 'containerRef'
  | 'onSortChange'
  | 'defaultSortState'
  | 'tableEmptyStateJsx'
  | 'isLoading'
  | 'variant'
  | 'sxRowFunction'
  | 'reachedLastRow'
  | 'hideChevronButton'
  | 'tableId'
> & {
  noTableBorder?: boolean;
}) {
  const [sortState, setSortState] = useState<DataTableSortStateProps | undefined>(defaultSortState);
  const [draggedOverColumnId, setDraggedOverColumnId] = useState<string | null>(null);
  const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null);
  const dragStateRef = useRef({
    draggedOverColumnId: null as string | null,
    draggedColumnId: null as string | null,
  });

  const _tableRef: RefObject<any> = useRef(null);
  const tableRef = containerRef ?? _tableRef;

  const { filteredAndSortedColumns } = useCustomColumnsTableContext();
  const { onColumnOrderChange } = useManipulateColumns(tableId as DataTableId);

  const handleColumnOrderChange = useCallback(
    ({ sourceIndex, destinationIndex }: { sourceIndex: number; destinationIndex: number }) => {
      const reorderedColumns = reorderItemInArray(
        filteredAndSortedColumns ?? [],
        sourceIndex,
        destinationIndex,
      );
      if (onColumnOrderChange) {
        onColumnOrderChange(reorderedColumns);
      }
    },
    [filteredAndSortedColumns, onColumnOrderChange],
  );

  const handleColumnDragOver = useCallback((columnId: string | null) => {
    dragStateRef.current.draggedOverColumnId = columnId;
    setDraggedOverColumnId(columnId);
  }, []);

  const handleColumnDragStart = useCallback((columnId: string) => {
    dragStateRef.current.draggedColumnId = columnId;
    setDraggedColumnId(columnId);
  }, []);

  const handleColumnDragEnd = useCallback(() => {
    dragStateRef.current.draggedColumnId = null;
    setDraggedColumnId(null);
  }, []);

  const _sort = useCallback(
    (columnName: string) => {
      const newState = {
        sortBy: columnName,
        sortOrder: sortState?.sortOrder === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC,
      };

      setSortState(newState);
      onSortChange && onSortChange(newState);
    },
    [onSortChange, sortState?.sortOrder],
  );

  const renderHeader = useCallback(() => {
    return (
      <DraggableTableHead
        columns={filteredAndSortedColumns ?? []}
        stickyHeader
        variant={variant}
        sort={_sort}
        sortState={sortState}
        onColumnOrderChange={handleColumnOrderChange}
        onDragOver={handleColumnDragOver}
        onDragStart={handleColumnDragStart}
        onDragEnd={handleColumnDragEnd}
        overColumnId={draggedOverColumnId}
        setOverColumnId={setDraggedOverColumnId}
        draggedColumnId={draggedColumnId}
        setDraggedColumnId={setDraggedColumnId}
        isColumnResizeAllowed={true}
      />
    );
  }, [
    filteredAndSortedColumns,
    _sort,
    sortState,
    variant,
    draggedOverColumnId,
    draggedColumnId,
    handleColumnOrderChange,
    handleColumnDragEnd,
    handleColumnDragOver,
    handleColumnDragStart,
  ]);

  const components = useMemo(() => {
    return {
      Table: forwardRef<any, TableProps>(({ children, style }, ref) => {
        return (
          <Table
            ref={ref}
            style={
              {
                '--virtuosoPaddingTop': (style?.paddingTop ?? 0) + 'px',
                '--virtuosoPaddingBottom': (style?.paddingBottom ?? 0) + 'px',
              } as any
            }
          >
            {children}
          </Table>
        );
      }),
      TableHead: forwardRef<any, TableProps>(({ children }, ref) => {
        return <StyledTableHead ref={ref}>{children}</StyledTableHead>;
      }),
      TableBody: forwardRef<any, TableProps>((props, ref) => {
        return (
          <StyledTableBody ref={ref} style={{ position: rows.length ? 'relative' : 'initial' }}>
            {!!rows.length ? (
              props.children
            ) : (
              <MuiTableRow
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  '&:hover': { backgroundColor: 'transparent !important' },
                }}
              >
                {tableEmptyStateJsx}
              </MuiTableRow>
            )}
          </StyledTableBody>
        );
      }),
      TableRow: forwardRef<any, TableRowProps>((props: any, ref) => {
        const row = props.item;
        const idx = props['data-index'];

        return (
          <>
            <TableRow
              hideChevronButton={hideChevronButton}
              data-index={idx}
              ref={ref}
              row={row}
              columns={filteredAndSortedColumns}
              sxRowFunction={sxRowFunction}
              tableRowProps={{ id: `table_row_${row.id}` }}
              onClick={
                onRowClick
                  ? (e, column) => {
                      onRowClick && onRowClick(e, row, column);
                    }
                  : undefined
              }
              variant={variant}
              overColumnId={dragStateRef.current.draggedOverColumnId}
              draggedColumnId={dragStateRef.current.draggedColumnId}
              isColumnReorderAllowed={true}
              isColumnResizeAllowed={true}
              {...props}
            />

            {reachedLastRow && idx === reachedLastRow?.rowsMaxCount - 1 && (
              <ReachedLastRow
                columnsCount={filteredAndSortedColumns?.length ?? 0}
                reachedLastRowTxt={reachedLastRow.txt}
                scrollToTop={() => {
                  (containerRef?.current as any)?.scrollToIndex({
                    index: 0,
                    top: 0,
                    behavior: 'smooth',
                  });
                }}
              />
            )}
          </>
        );
      }),
    };
  }, [
    tableEmptyStateJsx,
    filteredAndSortedColumns,
    onRowClick,
    rows.length,
    sxRowFunction,
    variant,
    containerRef,
    reachedLastRow,
    hideChevronButton,
  ]);

  return (
    <Stack
      height="100%"
      position="relative"
      sx={{
        border: noTableBorder ? 'none' : `1px solid ${colors.grey[120]}`,
        borderRadius: noTableBorder ? 'none' : '8px',
        overflowX: 'hidden',
      }}
    >
      {isLoading && <TableLoader />}
      <TableVirtuoso
        ref={tableRef}
        totalCount={rows.length}
        data={rows}
        components={components}
        fixedHeaderContent={renderHeader}
      />
    </Stack>
  );
}
